import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * ScrollToTop component - scrolls to top when navigating to a new page.
 * Does NOT scroll when only the hash fragment changes (like '/treasury#vault-holdings').
 * Used in App.js or router configuration component.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate(); // Not used directly for scroll, but good for context if needed later

  useEffect(() => {
    // If there's a hash, the browser will try to handle it.
    // We let our TreasuryPage specific logic handle smooth scrolling for its hashes.
    if (hash) {
      // Attempt to scroll to the element if it exists, useful for direct hash links
      try {
        const id = hash.substring(1);
        const element = document.getElementById(id);
        if (element) {
          // Use a timeout to ensure the element is rendered before scrolling
          // This is a fallback for direct hash navigation not handled by specific page logic
          const timeoutId = setTimeout(() => {
            element.scrollIntoView({ behavior: 'auto' }); // Use 'auto' for quicker jump
          }, 0); // Smallest possible timeout
          return () => clearTimeout(timeoutId);
        }
      } catch (e) {
        console.warn(`ScrollToTop: Could not scroll to hash ${hash}`, e);
      }
    } else {
      // If no hash, scroll to the top of the page.
      // This needs to be aggressive to override default browser behavior on route changes.
      document.documentElement.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto' // Use 'auto' for an immediate jump to the top
      });
      // Fallback for some browsers/cases
      document.body.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto'
      });
    }
  }, [pathname, hash, navigate]); // Add navigate to dependencies, though not directly used for scroll

  return null; // This component doesn't render anything
};

export default ScrollToTop; 